/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";

import { BrowserLogger } from "../common/BrowserLogger";
import { IEditor } from "../IEditor";
import { ErrorTracker } from "@adapptio/lib-components";

export type TStateValue = unknown;

interface IStateData {
	[K: string]: TStateValue;
}

/**
 * Service options
 */
export interface IStateServiceOpts {
	storagekey: string;
}

/**
 * Service to manage editor state
 */
export class StateService {
	/** Logger facility */
	private logger: BrowserLogger;

	/** Storage key */
	private storageKey: string;

	/** Registered keys */
	private keyIndex: {
		[K: string]: {
			default: unknown;
		};
	} = {};

	/** Config data */
	private data: IStateData = {};

	/** Config changed event */
	public onStateChanged = createEventEmitter<{ key: string }>();

	/**
	 * Resource Service Constructor
	 *
	 * @param dc Dependency Container
	 * @param opts Configuration options
	 */
	public constructor(dc: IEditor, opts: IStateServiceOpts) {
		this.logger = dc.get("logger").facility("StateService");
		this.storageKey = opts.storagekey;
	}

	/**
	 * Returs full item key for local storage
	 *
	 * @param itemKey Item key
	 */
	private getItemStorageKey(itemKey: string): string {
		return this.storageKey + "_" + itemKey;
	}

	/**
	 * Registers config key
	 *
	 * @param key Key
	 * @param defaultValue Default value
	 */
	public register<T>(key: string, defaultValue: T): void {
		if (this.keyIndex[key]) {
			throw new Error(`State key '${key}' is already registered.`);
		}

		this.keyIndex[key] = {
			default: defaultValue
		};

		this.data[key] = defaultValue;
	}

	/**
	 * Returns the state value by key
	 *
	 * @param key Key
	 */
	public get<T>(key: string): T {
		if (!this.keyIndex[key]) {
			throw new Error(`State key '${key}' is not registered.`);
		}

		return this.data[key] as T;
	}

	/**
	 * Stores the config under given key
	 *
	 * @param key Key
	 * @param value Value
	 */
	public set<T>(key: string, value: T): void {
		if (!this.keyIndex[key]) {
			throw new Error(`State key '${key}' is not registered.`);
		}

		this.data[key] = value;

		const storageKey = this.getItemStorageKey(key);
		localStorage.setItem(storageKey, JSON.stringify(value));

		this.emitStateChange({ key: key });
	}

	/**
	 * Returns if the key is registered
	 *
	 * @param key Key
	 */
	public has(key: string): boolean {
		return this.keyIndex[key] ? true : false;
	}

	/**
	 * Returns all state data
	 */
	public getEntireState(): IStateData {
		return this.data;
	}

	/**
	 * Tries to restore all registered state keys from local storage
	 */
	public restore(): void {
		for (const k in this.keyIndex) {
			const storageKey = this.getItemStorageKey(k);
			const storageData = localStorage.getItem(storageKey);

			if (storageData !== null) {
				try {
					this.data[k] = JSON.parse(storageData);
				} catch (err) {
					this.logger.warn(`Failed to parse state data for key '${k}':`, err);
				}
			} else {
				this.data[k] = this.keyIndex[k].default;
			}
		}

		this.logger.debug("State restored from local storage:", this.data);
		this.emitStateChange({ key: null });
	}

	private emitStateChange(eventData: { key: string | null }): void {
		emitEvent(this.onStateChanged, eventData);

		const errorTracker = ErrorTracker.getInstance();
		const state = Object.keys(this.data).reduce(
			(acc, key) => ({
				...acc,
				[key]: {
					value: this.data[key]
				}
			}),
			{}
		);
		errorTracker.setState(state);
	}
}
